import { DeleteDialog, GlobalMenu } from '@components';
import { AssetContext } from '@pages/Asset/Asset.query';
import { useContext } from 'react';
import { useState } from '@hookstate/core';
import { NavigationContext } from '@layout/navigation/navigation';
import { deleteResource } from '@services/queries/resources';
import { getPermissionData } from '@utils';
import { useQueryClient, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { IResource } from '@models/Resource.model';
import { getProductValue } from '@utils/getProductValue';
import { Container } from './ResourcesItemMenu.styles';

interface IResourcesItemMenu {
  resource: IResource;
}

const ResourcesItemMenu = ({ resource }: IResourcesItemMenu) => {
  const params = useParams<{ equipmentId: string }>();

  const { openEditResource, selectedResource, equipmentQuery } =
    useContext(AssetContext);
  const { role } = useContext(NavigationContext);

  const queryClient = useQueryClient();
  const openDeleteDialog = useState(false);

  const mutation = useMutation(deleteResource, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'resources',
      ]);
      openDeleteDialog.set(false);
    },
  });

  const editPermission = getPermissionData({
    owner: Boolean(resource.ownerStudioId),
    action: 'edit',
    role,
    type: 'Resource',
  });

  const deletePermission = getPermissionData({
    owner: Boolean(resource.ownerStudioId),
    action: 'delete',
    role,
    type: 'Resource',
  });

  const { manufacturerId, productId } = getProductValue(equipmentQuery?.data);

  const hasProduct = manufacturerId && productId;
  const isEnabled = editPermission.allowed && hasProduct;

  return (
    <Container>
      <GlobalMenu
        align="end"
        title="Options"
        itemList={[
          {
            label: 'Edit',
            iconName: 'edit',
            disabled: !isEnabled,
            toolTipText: editPermission.message,
            toolTipId: 'editDocumentTooltip',
            disableToolTip: editPermission.allowed,
            onClick: () => {
              if (isEnabled) {
                selectedResource.set(resource);
                openEditResource.set(true);
              }
            },
          },
          {
            label: 'Delete',
            iconName: 'trash',
            disabled: !deletePermission.allowed,
            disableToolTip: deletePermission.allowed,
            toolTipText: deletePermission.message,
            toolTipId: 'deleteDocumentTooltip',
            onClick: () => {
              if (deletePermission.allowed) {
                openDeleteDialog.set(true);
              }
            },
          },
        ]}
      />

      <DeleteDialog
        open={openDeleteDialog}
        onDelete={() => {
          mutation.mutate(resource.id);
        }}
      />
    </Container>
  );
};

export { ResourcesItemMenu };
